import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/Login.css";
import { 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon,
  CheckCircle,
  MarkEmailRead,
  DevicesOther
} from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';

function getOrCreateDeviceToken() {
  const KEY = 'pdx_device_token';
  let token = localStorage.getItem(KEY);
  if (!token) {
    if (window.crypto && window.crypto.randomUUID) {
      token = window.crypto.randomUUID();
    } else {
      token = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (window.crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
    localStorage.setItem(KEY, token);
  }
  return token;
}

const Login = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [awaitingIpValidation, setAwaitingIpValidation] = useState(false);
  // La checkbox est pré-cochée si un device token existe déjà dans localStorage
  const [trustDevice, setTrustDevice] = useState(() => !!localStorage.getItem('pdx_device_token'));

  // Détecter le retour après validation d'IP par email
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ip_validated') === '1') {
      setMessage({ type: 'success', text: 'Adresse IP autorisée ! Vous pouvez maintenant vous connecter.' });
    } else if (params.get('ip_changed') === '1') {
      setMessage({ type: 'warning', text: 'Votre IP a changé depuis votre dernière connexion. Veuillez vous reconnecter pour valider votre nouvelle adresse IP.' });
    } else if (params.get('ip_error') === 'expired') {
      setMessage({ type: 'error', text: 'Le lien de validation a expiré (24h). Reconnectez-vous pour recevoir un nouvel email.' });
    } else if (params.get('ip_error')) {
      setMessage({ type: 'error', text: 'Lien de validation invalide ou déjà utilisé.' });
    }
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      // N'envoyer le device token que si l'utilisateur a coché "Ne plus demander"
      const deviceToken = trustDevice ? getOrCreateDeviceToken() : null;
      const body = deviceToken
        ? { ...formData, device_token: deviceToken }
        : { ...formData };
      const response = await fetch(API_URL + "/account/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: "success", text: "Connexion réussie !" });
        
        // Ajout des votes lors de la sauvegarde locale
        localStorage.setItem('user', JSON.stringify({
          id:       data.account.guid,
          username: data.account.account,
          email:    data.account.email,
          ogrines:  data.account.ogrines || 0,
          votes:    data.account.votes || data.account.monthly_votes || 0
        }));

        if (data.sessionToken) {
          localStorage.setItem('sessionToken', data.sessionToken);
        }

        setTimeout(() => { navigate('/'); window.location.reload(); }, 1000);

      } else if (response.status === 403) {
        const data = await response.json();
        if (data.requires_ip_validation) {
          setAwaitingIpValidation(true);
          setMessage({ type: 'warning', text: data.message });
        } else {
          setMessage({ type: "error", text: data.error || "Accès refusé" });
        }
      } else {
        const errorData = await response.json();
        setMessage({ type: "error", text: errorData.error });
      }
    } catch (error) {
      console.error("Erreur lors de la connexion :", error);
      setMessage({ type: "error", text: "Erreur lors de la connexion." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page">
      <Container maxWidth="sm" sx={{ paddingTop: '120px', paddingBottom: '60px' }}>
        <Paper 
          elevation={8}
          className="login-card"
          sx={{
            padding: 4,
            background: 'linear-gradient(145deg, rgba(26, 10, 46, 0.9) 0%, rgba(20, 25, 45, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(182, 122, 60, 0.2)',
            borderRadius: '20px'
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
            <LoginIcon 
              sx={{ 
                fontSize: 60, 
                color: '#B67A3C',
                marginBottom: 2
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 1
              }}
            >
              Connexion
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Connectez-vous à <strong>Nexus</strong>
            </Typography>
          </Box>

          {message && (
            <Alert 
              severity={message.type} 
              sx={{ 
                marginBottom: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: message.type === 'success' ? '#B67A3C' : undefined
                }
              }}
              icon={message.type === 'success' ? <CheckCircle /> : message.type === 'warning' ? <MarkEmailRead /> : undefined}
            >
              {message.text}
            </Alert>
          )}

          {/* Écran "vérifiez votre email" */}
          {awaitingIpValidation && (
            <Box sx={{
              textAlign: 'center',
              padding: 3,
              background: 'rgba(182, 122, 60, 0.07)',
              border: '1px solid rgba(182, 122, 60, 0.25)',
              borderRadius: 3,
              marginBottom: 3,
            }}>
              <MarkEmailRead sx={{ fontSize: 52, color: '#B67A3C', mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#e0c8ff', fontWeight: 700, mb: 1 }}>
                Vérifiez votre email
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                Cliquez sur le lien envoyé à votre adresse email pour autoriser cette adresse IP, puis revenez vous connecter.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setAwaitingIpValidation(false); setMessage(null); }}
                sx={{ color: '#B67A3C', borderColor: 'rgba(182,122,60,0.4)', fontSize: '0.8rem' }}
              >
                Réessayer
              </Button>
            </Box>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <TextField 
              label="Nom d'utilisateur"
              name="username"
              variant="outlined"
              fullWidth
              required
              value={formData.username}
              onChange={handleChange}
              sx={{ marginBottom: 3 }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <TextField 
              label="Mot de passe"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              value={formData.password}
              onChange={handleChange}
              sx={{ marginBottom: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  size="small"
                  sx={{
                    color: 'rgba(182,122,60,0.5)',
                    '&.Mui-checked': { color: '#B67A3C' },
                    padding: '4px 8px'
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <DevicesOther sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.3 }}>
                    Ne plus demander sur cet appareil
                  </Typography>
                </Box>
              }
              sx={{ mb: 3, ml: 0, alignItems: 'flex-start' }}
            />

            <Button 
              variant="contained" 
              type="submit" 
              fullWidth 
              size="large"
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)',
                color: '#ffffff',
                fontWeight: 700,
                padding: '14px',
                fontSize: '1.1rem',
                marginBottom: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(182, 122, 60, 0.4)'
                },
                '&:disabled': {
                  background: 'rgba(182, 122, 60, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Se connecter"}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1.5 }}>
                <MuiLink 
                  component={Link} 
                  to="/forgot-password" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.85rem',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#B67A3C',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Mot de passe oublié ?
                </MuiLink>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Pas encore de compte ?{' '}
                <MuiLink 
                  component={Link} 
                  to="/signup" 
                  sx={{ 
                    color: '#B67A3C',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Créer un compte
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;